import '@mui/x-date-pickers/themeAugmentation';
import { alpha, createTheme, darken, lighten } from '@mui/material/styles';
import {
  breakpoints,
  colors,
  radii,
  shadows,
  spacing,
  typography,
  sizes,
  zIndex,
  opacity,
  motion,
  containers,
  density,
  type DensityMode,
  iconSizes,
} from './tokens';
import type { Shadows, Theme } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark';

type AppPalette = typeof colors.light | typeof colors.dark;

export const createAppTheme = (mode: ThemeMode, densityMode: DensityMode = 'standard'): Theme => {
  const palette: AppPalette = mode === 'light' ? colors.light : colors.dark;
  const primaryText = mode === 'dark' ? lighten(palette.primary, 0.2) : palette.primary;
  const successMain = palette.success;
  const successDark = darken(successMain, 0.2);
  const successLight = lighten(successMain, 0.2);
  const dangerMain = palette.danger;
  const dangerDark = darken(dangerMain, 0.2);
  const dangerLight = lighten(dangerMain, 0.2);
  const densityConfig = density[densityMode];
  const flatShadows = Array(25).fill('none') as unknown as Shadows;
  const opacityDisabled = opacity.disabled;
  const state: Theme['app']['state'] = {
    hover: alpha(palette.text, 0.06),
    active: alpha(palette.text, 0.12),
    focus: alpha(palette.primary, 0.24),
    disabled: alpha(palette.text, opacityDisabled),
    disabledBg: alpha(palette.text, 0.04),
  };

  return createTheme({
    palette: {
      mode,
      primary: { main: palette.primary, light: primaryText },
      success: { main: successMain, dark: successDark, light: successLight },
      warning: { main: palette.warning },
      error: {
        main: dangerMain,
        dark: dangerDark,
        light: dangerLight,
        contrastText: '#000000',
      },
      info: { main: palette.info },
      neutral: { main: palette.neutral },
      green: { main: palette.success },
      red: { main: palette.danger },
      orange: { main: palette.warning },
      brown: { main: palette.brown },
      blue: { main: palette.info },
      purple: { main: palette.purple },
      background: { default: palette.bg, paper: palette.surface },
      text: { primary: palette.text, secondary: palette.textMuted },
      divider: palette.divider,
      action: {
        hover: state.hover,
        selected: state.active,
        focus: state.focus,
        disabled: state.disabled,
        disabledBackground: state.disabledBg,
      },
    },
    breakpoints: { values: breakpoints },
    shape: { borderRadius: radii.md },
    typography: {
      fontFamily: typography.body.fontFamily,
      h1: {
        fontFamily: typography.pageTitle.fontFamily,
        fontSize: 31,
        lineHeight: 1.1,
        fontWeight: 500,
      },
      h2: {
        fontFamily: typography.sectionTitle.fontFamily,
        fontSize: 23,
        lineHeight: 1.25,
        fontWeight: 500,
      },
      h3: {
        fontFamily: typography.sectionTitle.fontFamily,
        fontSize: 19,
        lineHeight: 1.3,
        fontWeight: 500,
      },
      h4: {
        fontFamily: typography.sectionTitle.fontFamily,
        fontSize: 17,
        lineHeight: 1.35,
        fontWeight: 500,
      },
      h5: {
        fontFamily: typography.sectionTitle.fontFamily,
        fontSize: 16,
        lineHeight: 1.35,
        fontWeight: 500,
      },
      h6: {
        fontFamily: typography.sectionTitle.fontFamily,
        fontSize: 19,
        lineHeight: 1.4,
        fontWeight: 500,
      },
      subtitle1: {
        fontFamily: typography.body.fontFamily,
        fontSize: 15,
        lineHeight: 1.4,
        fontWeight: 500,
      },
      subtitle2: {
        fontFamily: typography.body.fontFamily,
        fontSize: 13,
        lineHeight: 1.4,
        fontWeight: 500,
      },
      body1: {
        fontFamily: typography.body.fontFamily,
        fontSize: 15,
        lineHeight: 1.6,
        fontWeight: 400,
      },
      body2: {
        fontFamily: typography.body.fontFamily,
        fontSize: 13,
        lineHeight: 1.5,
        fontWeight: 400,
      },
      caption: {
        fontFamily: typography.body.fontFamily,
        fontSize: 11,
        lineHeight: 1.4,
        fontWeight: 400,
      },
      overline: {
        fontFamily: typography.body.fontFamily,
        fontSize: 10,
        lineHeight: 1.4,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      button: {
        fontFamily: typography.body.fontFamily,
        fontSize: 15,
        lineHeight: 1.4,
        fontWeight: 600,
        textTransform: 'none',
      },
      pageTitle: typography.pageTitle,
      sectionTitle: typography.sectionTitle,
      body: typography.body,
      bodyMuted: { ...typography.bodyMuted, color: palette.textMuted },
      label: typography.label,
      mono: typography.mono,
    },
    shadows: flatShadows,
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            pageTitle: 'h1',
            sectionTitle: 'h2',
            body: 'p',
            bodyMuted: 'p',
            label: 'span',
            caption: 'span',
            mono: 'code',
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: { '&.Mui-focusVisible': { outline: `2px solid ${state.focus}`, outlineOffset: 2 } },
        },
        defaultProps: { disableRipple: true },
      },
      MuiButton: {
        defaultProps: { size: densityConfig.buttonSize, disableRipple: true },
        styleOverrides: {
          root: {
            transition: `background-color ${motion.durations.fast} ${motion.easing.standard}`,
            textTransform: 'none',
            '&:hover': { backgroundColor: 'transparent', boxShadow: 'none' },
          },
          contained: {
            '&:hover': { backgroundColor: palette.primary, boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: state.disabledBg },
          },
          containedError: {
            backgroundColor: dangerDark,
            color: '#000000',
            '&:hover': { backgroundColor: dangerDark, boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: state.disabledBg },
          },
          outlined: {
            color: primaryText,
            borderColor: alpha(primaryText, 0.4),
            '&:hover': { backgroundColor: 'transparent' },
          },
          text: { color: primaryText, '&:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiLink: {
        styleOverrides: { root: { color: primaryText } },
      },
      MuiIconButton: {
        defaultProps: { disableRipple: true },
      },
      MuiPaper: { defaultProps: { elevation: 0 } },
      MuiTextField: { defaultProps: { size: densityConfig.inputSize } },
      MuiSelect: { defaultProps: { size: densityConfig.inputSize } },
      MuiMenu: {
        styleOverrides: {
          list: {
            paddingTop: spacing.none,
            paddingBottom: spacing.none,
            '&:has(.MuiTablePagination-menuItem)': {
              paddingTop: spacing.none,
              paddingBottom: spacing.none,
            },
          },
          paper: {
            borderRadius: radii.md,
            overflow: 'hidden',
            '&:has(.MuiTablePagination-menuItem)': {
              backgroundColor: palette.surface,
              backgroundImage: 'none',
              border: `1px solid ${palette.divider}`,
              boxShadow: 'none',
              '--Paper-overlay': 'none',
              '--Paper-shadow': 'none',
              marginTop: spacing.xxs,
              borderRadius: radii.md,
              overflow: 'hidden',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 15,
            lineHeight: 1.4,
            fontWeight: 500,
            '& .MuiTypography-root, & .MuiListItemText-primary, & span': {
              fontSize: 15,
              lineHeight: 1.4,
              fontWeight: 500,
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          option: {
            fontSize: 15,
            lineHeight: 1.4,
            fontWeight: 500,
            '& .MuiTypography-root, & span': {
              fontSize: 15,
              lineHeight: 1.4,
              fontWeight: 500,
            },
          },
        },
      },
      MuiPopover: { styleOverrides: { paper: { borderRadius: radii.md, overflow: 'hidden' } } },
      MuiTablePagination: {
        styleOverrides: {
          actions: { '& .MuiIconButton-root:hover': { backgroundColor: 'transparent' } },
          menuItem: {
            color: palette.text,
            backgroundColor: `${palette.surface} !important`,
            minHeight: spacing['4xl'],
            paddingTop: spacing.none,
            paddingBottom: spacing.none,
            paddingLeft: spacing.md,
            paddingRight: spacing.md,
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            fontFamily: typography.body.fontFamily,
            fontSize: typography.body.fontSize + 2,
            fontWeight: typography.body.fontWeight,
            lineHeight: typography.body.lineHeight,
            border: '0 !important',
            '&:hover': {
              backgroundColor: `${state.hover} !important`,
            },
            '&.Mui-selected': {
              backgroundColor: `${palette.surface} !important`,
            },
            '&.Mui-selected:hover': {
              backgroundColor: `${state.hover} !important`,
            },
            '&.Mui-focusVisible': {
              backgroundColor: `${state.hover} !important`,
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: { root: { '&:hover': { backgroundColor: 'transparent' } } },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: { '& .MuiIconButton-root:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          root: { '& .MuiIconButton-root:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: { switchViewButton: { '&:hover': { backgroundColor: 'transparent' } } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            transition: `border-color ${motion.durations.fast} ${motion.easing.standard}`,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary,
              borderWidth: 2,
            },
            '&.Mui-disabled': { backgroundColor: state.disabledBg, opacity: opacity.disabled },
          },
          input: {
            '&:not(.MuiInputBase-inputMultiline)': {
              boxSizing: 'border-box',
              paddingTop: spacing.none,
              paddingBottom: spacing.none,
              paddingLeft: spacing.lg,
              paddingRight: spacing.md,
            },
          },
        },
      },
      MuiCheckbox: { styleOverrides: { root: { '&:hover': { backgroundColor: 'transparent' } } } },
      MuiSwitch: {
        styleOverrides: {
          root: { '&:hover': { backgroundColor: 'transparent' } },
          switchBase: { '&:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiChip: { styleOverrides: { root: {} } },
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundColor: palette.surface },
        },
      },
    },
    app: {
      colors: palette,
      radii,
      shadows: shadows[mode],
      spacing,
      breakpoints,
      sizes,
      zIndex,
      opacity,
      motion,
      containers,
      density: densityConfig,
      iconSizes,
      state,
    },
  });
};
